import React, { useState, useEffect } from 'react';
import { 
  Car, 
  Gauge, 
  Fuel, 
  Ruler, 
  Settings, 
  Calendar,
  TrendingUp,
  Shield,
  Zap,
  Users
} from 'lucide-react';
import { api } from '../services/api';

const VehicleSpecificationsDisplay = ({ vehicleProfileId, premiumData }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (premiumData) {
      setData(premiumData);
    } else if (vehicleProfileId) {
      fetchPremiumData();
    }
  }, [vehicleProfileId, premiumData]);

  const fetchPremiumData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/vehicle-profiles/${vehicleProfileId}/premium-data`);
      
      if (response.status === 200) {
        const result = response.data;
        setData(result);
      }
    } catch (error) {
      console.error('Error fetching premium data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
        <span className="ml-2 text-ink-muted">Loading vehicle specifications...</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center p-8 text-ink-muted">
        <Car className="w-12 h-12 mx-auto mb-4 text-ink-muted" />
        <p>No premium vehicle data available</p>
      </div>
    );
  }

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const isBlank = (value) => value === null || value === undefined || value === '';
  const withUnit = (unit) => (value) => `${value}${unit}`;
  const price = (value) => (value ? formatPrice(value) : null);
  const litres = (value) => `${Number(value).toFixed(1)} L`;

  // Each card lists [label, value, format?]. A row with no value is dropped
  // and a card with no rows is not drawn: an NHTSA decode only knows the
  // vehicle's identity, engine size, fuel, transmission and drivetrain, and
  // every card used to render anyway, as a wall of "N/A".
  const info = data.vehicle_info || {};
  const engine = data.performance?.engine || {};
  const transmission = data.performance?.transmission || {};
  const economy = data.performance?.fuel_economy || {};
  const braking = data.braking || {};
  const wheels = data.wheels_and_tires || {};
  const exterior = data.dimensions?.exterior || {};
  const interior = data.dimensions?.interior || {};
  const cargo = data.dimensions?.cargo || {};
  const weights = data.dimensions?.weights || {};

  const cards = [
    { title: 'Vehicle', icon: Car, tone: 'text-accent', rows: [
      ['Year', info.year], ['Make', info.make], ['Model', info.model], ['Trim', info.trim],
      ['Body', info.body_type], ['Type', info.vehicle_type], ['Drivetrain', info.drivetrain],
      ['Built in', info.country_of_origin],
    ] },
    { title: 'Engine', icon: Zap, tone: 'text-accent', rows: [
      ['Type', engine.type], ['Displacement', engine.displacement, litres], ['Cylinders', engine.cylinders],
      ['Fuel', engine.fuel_type], ['Horsepower', engine.horsepower, withUnit(' hp')],
      ['Torque', engine.torque, withUnit(' lb-ft')], ['Compression', engine.compression_ratio],
    ] },
    { title: 'Transmission', icon: Settings, tone: 'text-success', rows: [
      ['Type', transmission.type], ['Speeds', transmission.speeds], ['Description', transmission.description],
    ] },
    { title: 'Fuel Economy', icon: Fuel, tone: 'text-warning', rows: [
      ['City', economy.epa_city, withUnit(' mpg')], ['Highway', economy.epa_hwy, withUnit(' mpg')],
      ['Combined', economy.epa_combined, withUnit(' mpg')],
    ] },
    { title: 'Seating', icon: Users, tone: 'text-info', rows: [
      ['Capacity', data.seating?.capacity], ['Max Capacity', data.seating?.max_capacity],
    ] },
    { title: 'Pricing', icon: TrendingUp, tone: 'text-success', rows: [
      ['Base MSRP', price(info.base_msrp)], ['Total Price', price(info.total_price)],
    ] },
    { title: 'Emissions', icon: Shield, tone: 'text-danger', rows: [
      ['Greenhouse Gas', data.emissions?.greenhouse_gas_score],
      ['Air Pollution', data.emissions?.air_pollution_score],
    ] },
    { title: 'Braking System', icon: Gauge, tone: 'text-danger', rows: [
      ['Front Type', braking.front_type], ['Rear Type', braking.rear_type],
      ['Front Rotor', braking.front_rotor_diameter, withUnit(' in')],
      ['Rear Rotor', braking.rear_rotor_diameter, withUnit(' in')], ['ABS System', braking.abs_system],
    ] },
    { title: 'Wheels & Tires', icon: Car, tone: 'text-ink-muted', rows: [
      ['Front Tire', wheels.front_tire_size], ['Rear Tire', wheels.rear_tire_size],
      ['Front Wheel', wheels.front_wheel_size], ['Rear Wheel', wheels.rear_wheel_size],
    ] },
    { title: 'Dimensions', icon: Ruler, tone: 'text-info', rows: [
      ['Length', exterior.length_in, withUnit(' in')], ['Width', exterior.width_in, withUnit(' in')],
      ['Height', exterior.height_in, withUnit(' in')], ['Wheelbase', exterior.wheelbase_in, withUnit(' in')],
      ['Front Head Room', interior.front_head_room_in, withUnit(' in')],
      ['Rear Head Room', interior.rear_head_room_in, withUnit(' in')],
      ['Front Leg Room', interior.front_leg_room_in, withUnit(' in')],
      ['Rear Leg Room', interior.rear_leg_room_in, withUnit(' in')],
      ['Cargo Volume', cargo.cargo_volume_cuft, withUnit(' cu ft')],
      ['Curb Weight', weights.curb_weight_lb, withUnit(' lbs')],
      ['Payload Capacity', weights.payload_capacity_lb, withUnit(' lbs')],
    ] },
  ]
    .map((card) => ({ ...card, rows: card.rows.filter(([, value]) => !isBlank(value)) }))
    .filter((card) => card.rows.length > 0);

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Vehicle Summary */}
      {info.summary && (
        <div className="bg-info-tint rounded-lg p-6">
          <h3 className="text-lg font-semibold text-ink-soft mb-3">Vehicle Summary</h3>
          <p className="text-ink-soft leading-relaxed">{info.summary}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map(({ title, icon: Icon, tone, rows }) => (
          <div key={title} className="bg-surface-card rounded-card shadow-elevation-1 border border-border-default p-6">
            <div className="flex items-center mb-4">
              <Icon className={`w-5 h-5 ${tone} mr-2`} />
              <h4 className="font-semibold text-ink-soft">{title}</h4>
            </div>
            <div className="space-y-2 text-sm">
              {rows.map(([label, value, format]) => (
                <div key={label} className="flex justify-between gap-4">
                  <span className="text-ink-muted">{label}:</span>
                  <span className="font-medium text-right">{format ? format(value) : value}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Colors Section */}
      {(data.colors?.exterior?.length > 0 || data.colors?.interior?.length > 0) && (
        <div className="bg-surface-card rounded-card shadow-elevation-1 border border-border-default p-6">
          <h3 className="text-lg font-semibold text-ink-soft mb-4">Available Colors</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Exterior Colors */}
            {data.colors.exterior && data.colors.exterior.length > 0 && (
              <div>
                <h4 className="font-medium text-ink-soft mb-3">Exterior Colors</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.colors.exterior.slice(0, 6).map((color, index) => (
                    <div key={index} className="border border-border-default rounded-lg p-3">
                      <div className="flex items-center mb-1">
                        <div 
                          className="w-3 h-3 rounded-full mr-2 border border-border-default"
                          style={{ backgroundColor: color.hex_value }}
                        ></div>
                        <span className="font-medium text-small">{color.generic_name}</span>
                      </div>
                      <p className="text-small text-ink-muted">{color.color_name}</p>
                      {color.color_price > 0 && (
                        <p className="text-small text-success font-medium">
                          +{formatPrice(color.color_price)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Interior Colors */}
            {data.colors.interior && data.colors.interior.length > 0 && (
              <div>
                <h4 className="font-medium text-ink-soft mb-3">Interior Colors</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.colors.interior.slice(0, 6).map((color, index) => (
                    <div key={index} className="border border-border-default rounded-lg p-3">
                      <div className="flex items-center mb-1">
                        <div 
                          className="w-3 h-3 rounded-full mr-2 border border-border-default"
                          style={{ backgroundColor: color.hex_value }}
                        ></div>
                        <span className="font-medium text-small">{color.generic_name}</span>
                      </div>
                      <p className="text-small text-ink-muted">{color.color_name}</p>
                      {color.color_price > 0 && (
                        <p className="text-small text-success font-medium">
                          +{formatPrice(color.color_price)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Features Section */}
      {(data.features?.standard?.length > 0 || data.features?.optional?.length > 0) && (
        <div className="bg-surface-card rounded-card shadow-elevation-1 border border-border-default p-6">
          <h3 className="text-lg font-semibold text-ink-soft mb-4">Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Standard Features */}
            {data.features.standard && data.features.standard.length > 0 && (
              <div>
                <h4 className="font-medium text-ink-soft mb-3">Standard Features</h4>
                <div className="space-y-2">
                  {data.features.standard.slice(0, 10).map((feature, index) => (
                    <div key={index} className="flex items-center text-sm">
                      <div className="w-2 h-2 bg-success rounded-full mr-2"></div>
                      <span>{feature.feature_name}</span>
                    </div>
                  ))}
                  {data.features.standard.length > 10 && (
                    <p className="text-small text-ink-muted mt-2">
                      +{data.features.standard.length - 10} more standard features
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Optional Features */}
            {data.features.optional && data.features.optional.length > 0 && (
              <div>
                <h4 className="font-medium text-ink-soft mb-3">Optional Features</h4>
                <div className="space-y-2">
                  {data.features.optional.slice(0, 10).map((feature, index) => (
                    <div key={index} className="flex items-center text-sm">
                      <div className="w-2 h-2 bg-accent rounded-full mr-2"></div>
                      <span>{feature.feature_name}</span>
                    </div>
                  ))}
                  {data.features.optional.length > 10 && (
                    <p className="text-small text-ink-muted mt-2">
                      +{data.features.optional.length - 10} more optional features
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Maintenance Schedule */}
      {data.maintenance_schedule && data.maintenance_schedule.length > 0 && (
        <div className="bg-surface-card rounded-card shadow-elevation-1 border border-border-default p-6">
          <h3 className="text-lg font-semibold text-ink-soft mb-4">Recommended Maintenance Schedule</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.maintenance_schedule.slice(0, 8).map((item, index) => (
              <div key={index} className="border border-border-default rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-medium text-ink-soft text-sm">{item.service_name}</h4>
                  <span className="text-small text-ink-muted">{item.service_category}</span>
                </div>
                {item.service_description && (
                  <p className="text-small text-ink-muted mb-2">{item.service_description}</p>
                )}
                <div className="flex justify-between text-small">
                  <span className="text-ink-muted">
                    {isBlank(item.recommended_interval_miles) ? 'N/A' : `${item.recommended_interval_miles} miles`}
                  </span>
                  <span className="text-ink-muted">
                    {formatPrice(item.estimated_cost_min)} - {formatPrice(item.estimated_cost_max)}
                  </span>
                </div>
              </div>
            ))}
            {data.maintenance_schedule.length > 8 && (
              <div className="col-span-full text-center p-4 text-ink-muted">
                <Calendar className="w-8 h-8 mx-auto mb-2 text-ink-muted" />
                <p className="text-sm">+{data.maintenance_schedule.length - 8} more maintenance items</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto">
      {/* Vehicle Specifications */}
      <div className="mb-6">
        <h2 className="text-page-title text-ink-soft mb-2">Vehicle Specifications</h2>
        <p className="text-ink-muted">Comprehensive details about your vehicle's performance, features, and specifications</p>
      </div>

      {/* Specifications Content */}
      <div className="min-h-96">
        {renderOverview()}
      </div>
    </div>
  );
};

export default VehicleSpecificationsDisplay; 